import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useMeeting } from '../contexts/MeetingContext';

const ViewMeeting = ({ open, onClose, meetingId, onEdit, onDelete }) => {
  const { fetchMeetingById, uploadAttachment, deleteAttachment, downloadAttachment, loading } = useMeeting();
  const [meeting, setMeeting] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && meetingId) {
      loadMeeting();
    }
  }, [open, meetingId]);

  const loadMeeting = async () => {
    try {
      const data = await fetchMeetingById(meetingId);
      setMeeting(data);
      setAttachments(data.attachments || []);
    } catch (error) {
      setError('Failed to load meeting details');
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let file of files) {
        // Simulate upload progress
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 100);

        const result = await uploadAttachment(meetingId, file);
        
        clearInterval(progressInterval);
        setUploadProgress(100);

        // Add uploaded file to local state (in demo mode)
        const newAttachment = {
          id: Date.now(),
          file_name: file.name,
          original_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          created_at: new Date().toISOString()
        };
        setAttachments(prev => [...prev, newAttachment]);
        
        setTimeout(() => setUploadProgress(0), 1000);
      }
    } catch (error) {
      setError('Failed to upload file');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId, fileName) => {
    if (window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      try {
        await deleteAttachment(meetingId, attachmentId);
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
      } catch (error) {
        setError('Failed to delete attachment');
      }
    }
  };

  const handleDownloadAttachment = (attachmentId, fileName) => {
    downloadAttachment(meetingId, attachmentId, fileName);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'ongoing': return 'warning';
      case 'cancelled': return 'error';
      default: return 'primary';
    }
  };

  if (!meeting) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">{meeting.title}</Typography>
        <Box>
          <Tooltip title="Edit Meeting">
            <IconButton onClick={() => onEdit && onEdit(meeting)} color="primary">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Meeting">
            <IconButton onClick={() => onDelete && onDelete(meeting.id)} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Meeting Details */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Meeting Details</Typography>
                  <Chip 
                    label={meeting.status} 
                    color={getStatusColor(meeting.status)}
                    sx={{ ml: 'auto' }}
                  />
                </Box>

                {meeting.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {meeting.description}
                  </Typography>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {new Date(meeting.meeting_date).toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  {meeting.location && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">{meeting.location}</Typography>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        Organizer: {meeting.organizer_name || 'Unknown'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {meeting.agenda && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>Agenda</Typography>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {meeting.agenda}
                    </Typography>
                  </>
                )}

                {meeting.participants && meeting.participants.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>Participants ({meeting.participants.length})</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {meeting.participants.map((participant, index) => (
                        <Chip
                          key={index}
                          label={participant.full_name || `Participant ${participant.participant_id}`}
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Attachments */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Attachments ({attachments.length})
                  </Typography>
                  <Button
                    startIcon={<CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    size="small"
                  >
                    Upload
                  </Button>
                </Box>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.xlsx,.xls,.ppt,.pptx,.zip,.rar"
                />

                {isUploading && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Uploading...</Typography>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                )}

                {attachments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    No attachments yet
                  </Typography>
                ) : (
                  <List dense>
                    {attachments.map((attachment) => (
                      <ListItem key={attachment.id} divider>
                        <ListItemIcon>
                          <AttachFileIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={attachment.original_name}
                          secondary={`${formatFileSize(attachment.file_size)} • ${new Date(attachment.created_at).toLocaleDateString()}`}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Download">
                            <IconButton 
                              edge="end" 
                              size="small"
                              onClick={() => handleDownloadAttachment(attachment.id, attachment.original_name)}
                              sx={{ mr: 1 }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              edge="end" 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteAttachment(attachment.id, attachment.original_name)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>

            {/* Meeting Notes */}
            {meeting.notes && meeting.notes.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Notes</Typography>
                  {meeting.notes.map((note) => (
                    <Box key={note.id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" paragraph>
                        {note.note_content}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        By {note.created_by_name} on {new Date(note.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewMeeting;
